import { Injectable } from '@angular/core';
import { CookieOptions } from '../interfaces';
import { CookieService } from './cookie.service';
import { environment } from '@env/environment';

/**
 * A service to manage session cookies.
 *
 * These cookies are created when user is authenticated.
 * App may have access to the following cookies:
 *
 *  - `EZBO_SESSION_vdclive` contains the session Id.
 *  - `vc_uid` contains the user Id of currently signed-in user.
 */
@Injectable()
export class SessionCookieService {
  static readonly SESSION_COOKIE = environment.cookieSession;
  static readonly UID_COOKIE = 'vc_uid';

  cookieOptions: CookieOptions = {
    domain: environment.cookieOptions.domain,
    expires: 30,
    path: '/'
  };

  constructor(private cookieService: CookieService) {}

  hasUserId(): boolean {
    const userId = this.getUserId();

    return typeof userId === 'string';
  }

  /**
   * Reads the currently authenticated user Id from cookies.
   * @returns the user Id
   */
  getUserId(): string {
    return this.cookieService.get(SessionCookieService.UID_COOKIE);
  }

  /**
   * Stores currently authenticated user into a cookie.
   * @param userId the user Id
   */
  setUserId(userId: string): void {
    this.cookieService.set(SessionCookieService.UID_COOKIE, userId, this.cookieOptions);
  }

  /**
   * Checks if a session cookie exists.
   */
  hasSession(): boolean {
    const sessionId = this.getSessionId();

    return typeof sessionId === 'string';
  }

  /**
   * Reads the current session Id from cookies.
   * @returns the session Id
   */
  getSessionId(): string {
    return this.cookieService.get(SessionCookieService.SESSION_COOKIE);
  }

  /**
   * Stores the session Id into a cookie.
   * @param id the session Id
   */
  setSessionId(id: string): void {
    this.cookieService.set(SessionCookieService.SESSION_COOKIE, id, this.cookieOptions);
  }

  /**
   * Removes all session cookies.
   */
  clear(): void {
    this.cookieService.remove(SessionCookieService.SESSION_COOKIE, this.cookieOptions);
    this.cookieService.remove(SessionCookieService.UID_COOKIE, this.cookieOptions);
  }
}
